import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import OfferCardMapper from 'components/OfferList/OfferCards/OfferCardMapper'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import {
  calculateHasBeenInView,
  calculateTrackingInView,
  handleSkipInView,
} from 'components/utils/OfferInView'
import CarouselOfferCard from './CarouselOfferCardStyles'
import CarouselPricingSourceContext from './CarouselPricingSourceContext'
import HotelDetailPricing from 'tripPlanner/components/ItemOfferPrice/HotelDetailPricing'
import { mapDrawerHotelPricingFields } from 'tripPlanner/mappers/offerDetailSharedFields'
import type { BatchOfferDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import OfferListEventsContext, { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import ChatCruiseAddToTripAction from './ChatCruiseAddToTripAction'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import ChatHotelCarouselPrice from './ChatHotelCarouselPrice'
import { getHotelOfferMinNights } from 'tripPlanner/utils/hotelOfferMinNights'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import type { BatchUnavailableReason } from './useCarouselOfferSearch'

const HotelCardWrapper = styled.div`
  position: relative;
`

const SelectDatesButton = styled(TextButton)`
  position: relative;
  z-index: 2;
`

const ClickOverlay = styled(Clickable)`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
`

const AddToTripButtonContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 2;
`

const BookedLabelContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-start: ${rem(12)};
  z-index: 2;
`

interface Props {
  offerId: string
  offerDetail?: BatchOfferDetailResult
  index: number
  cardType?: 'hotel' | 'experience' | 'cruise'
  offerName?: string
  offerType?: string
  offerCity?: string
  offerCoordinates?: { lat: number; lng: number }
  itineraryData?: TripItineraryData
  isBooked?: boolean
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onOfferClick?: (offerId: string) => void
  onOfferHover?: (offerId: string | undefined) => void
  offerUrl?: string
  openDatePicker?: () => void
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filterNights?: number
  /**
   * The already-resolved offer this card renders. The carousel derives its rendered ids from
   * the same offer list, so in practice this is always present — it stays optional only so a
   * missing entry degrades to an empty card rather than a crash.
   */
  offer?: App.AnyOffer
  cruiseOffer?: App.CruiseOffer
  size?: 'small' | 'medium'
  isStreaming?: boolean
  className?: string
  /** True when `offer` came from svc-trip's already-priced batch rather than a client-side search. */
  isOfferDetailSource?: boolean
  /**
   * Set when svc-trip's batch reported this offer id as a per-offer failure. The card then
   * renders NOTHING — see the guard below. `useCarouselOfferSearch` already drops these ids,
   * so in practice this arrives undefined; it is passed so the guard stays live for a caller
   * that stops filtering.
   */
  unavailableReason?: BatchUnavailableReason
}

function ChatHotelCarouselCard({
  offerId,
  offerDetail,
  index,
  cardType = 'hotel',
  offerName,
  offerType,
  offerCity,
  offerCoordinates,
  itineraryData,
  isBooked,
  onSendMessage,
  onOfferClick,
  onOfferHover,
  offerUrl,
  openDatePicker,
  selectedDatesAndRoomsFilter,
  filterNights,
  offer,
  cruiseOffer,
  size = 'small',
  isStreaming = false,
  className,
  isOfferDetailSource = false,
  unavailableReason,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const { viewOfferCopy } = useTripPlannerCTALabels()
  const [skipInView, setSkipInView] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const [tileInViewRef, inView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, inView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])
  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  useEffect(() => {
    if (offer && trackingInView && imageLoaded) {
      dispatchOfferListEvent({
        offer,
        position: index,
        type: OfferListEvents.productImpression,
        key: EventDataKey.ProductImpression,
      })
    }
    // Keyed on the offer's ID, not the offer object. On the pre-edit path `offer` comes from a
    // `useMemo` over the batch results, so any parent render that changes the id array's identity
    // rebuilds every offer object — same offer, new reference. Depending on the object re-fired an
    // impression for every card still on screen on each of those renders, double-counting the very
    // surface this instruments. Pre-migration the offer came from Redux and was stable by
    // construction, so this exposure arrived with the migration.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView, offer?.id, imageLoaded])

  // The card's images live inside OfferCardMapper's children, so the only way to know they've
  // loaded is to intercept their `offerReady` on the way up to the carousel's tracking provider.
  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.offerReady) {
      setImageLoaded(true)
    }
    // Now that we've handled our case, propagate the event up to other listeners
    dispatchOfferListEvent(dispatchAction, additionalData)
  }, [dispatchOfferListEvent])

  const handleTrackedOfferClick = useCallback(() => {
    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer,
        position: index,
      })
    }
  }, [dispatchOfferListEvent, index, offer])

  const handleOverlayClick = useCallback(() => {
    handleTrackedOfferClick()
    onOfferClick?.(offerId)
  }, [handleTrackedOfferClick, onOfferClick, offerId])

  const handleDatePickerClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // CtaComponent renders inside OfferCard's own offer-link anchor, so we must
    // stop the click reaching that anchor or it'll navigate instead of opening the picker.
    event.stopPropagation()
    event.preventDefault()
    handleTrackedOfferClick()
    openDatePicker?.()
  }, [handleTrackedOfferClick, openDatePicker])

  const SelectDatesCta = useCallback(function SelectDatesCta() {
    return <SelectDatesButton kind="primary" onClick={handleDatePickerClick}>
      <FormattedMessage
        id="3sIzsG20FG0beNGBCQuq"
        defaultMessage="Select dates"
        description="Carousel price placeholder prompting the user to pick dates for an Always-On/SPP hotel whose price is date-dependent"
      />
    </SelectDatesButton>
  }, [handleDatePickerClick])

  const handleMouseEnter = useCallback(() => {
    onOfferHover?.(offerId)
  }, [onOfferHover, offerId])

  const handleMouseLeave = useCallback(() => {
    onOfferHover?.(undefined)
  }, [onOfferHover])

  const FlexibleBedbankPricing = useCallback(function FlexibleBedbankPricing() {
    if (offerDetail?.status !== 'ok') return null
    const detail = offerDetail.detail
    const pricing = mapDrawerHotelPricingFields(detail.pricing, detail.duration, detail.currencyCode)
    return <HotelDetailPricing
      {...pricing}
      offerId={offerId}
      saleUnit={(selectedDatesAndRoomsFilter?.rooms?.length ?? 1) > 1 ? 'total' : detail.saleUnit}
    />
  }, [offerDetail, offerId, selectedDatesAndRoomsFilter?.rooms?.length])

  const placeName = offerName || offerId

  const hasDates = !!(selectedDatesAndRoomsFilter?.checkIn && selectedDatesAndRoomsFilter?.checkOut) ||
    !!(selectedDatesAndRoomsFilter?.flexibleMonths && selectedDatesAndRoomsFilter?.flexibleNights)
  // Always-On/SPP and Bedbank hotels have no trustworthy no-date price, so withhold it
  // and prompt for dates (same treatment across both offer types).
  const isNoDatePriceWithheld = (offerType === OFFER_TYPE_ALWAYS_ON || offerType === OFFER_TYPE_BED_BANK) && !hasDates
  // Mirrors the shared offer-card stack's own lock decision (blurred image, padlock,
  // "Unlock via Free Preview"), so the click surface only disables when the tile
  // actually renders locked for this viewer — members must still be able to click through.
  // getIsLuxPlusHiddenOffer reads offer.luxPlus.access without an optional chain, so only
  // call it once offer.luxPlus is confirmed present.
  const isLockedOffer = useAppSelector(state => !!offer?.luxPlus && getIsLuxPlusHiddenOffer(state, offer))

  // Ported verbatim from OfferCard: with the offer handed in this is always `true`, but keeping
  // the expression means the shimmer stays paused if a card ever renders without one.
  const pauseLoader = !hasBeenInView || !!offer

  // An offer the batch could not fetch renders nothing at all.
  //
  // Deliberately redundant with `useCarouselOfferSearch`, which already drops these ids from
  // `filteredOfferIds` so this card never mounts for one. Two layers because the thing being
  // prevented is a specific, rejected UI: a nameless, imageless "Price unavailable" tile.
  // All four batch reasons (`timeout`, `upstream_error`, `not_found`, `unavailable`) mean WE
  // failed to fetch an offer that is usually perfectly bookable — not that it cannot be
  // booked — and the assistant's prose directly above the carousel often names that same
  // hotel with a price. `retry: false` then makes the contradiction permanent for the
  // message. Filtering alone would leave that tile one call-site change away from returning.
  //
  // Genuine unavailability is untouched: it arrives as `status: 'ok'` with the price withheld
  // inside the detail, so `offer` is present and the full card renders with
  // ChatHotelCarouselPrice showing the withheld-price state.
  //
  // Safe here rather than earlier: every hook above has already run, and the impression
  // effect is gated on `offer` being present, so nothing is tracked for a card that paints
  // nothing.
  if (!offer && unavailableReason) return null

  return (
    <HotelCardWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isLockedOffer && !unavailableReason && !offerUrl && onOfferClick && <ClickOverlay data-testid={`chat-offer-overlay-${offerId}`} onClick={handleOverlayClick} aria-label={viewOfferCopy} />}
      {!isLockedOffer && !unavailableReason && offerUrl && (
        <ClickOverlay
          data-testid={`chat-offer-overlay-${offerId}`}
          to={offerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleTrackedOfferClick}
          aria-label={viewOfferCopy}
        />
      )}
      {isBooked && (
        <BookedLabelContainer>
          <BookedLabel />
        </BookedLabelContainer>
      )}
      {onSendMessage && cardType === 'experience' && itineraryData && !isBooked && (
        <AddToTripButtonContainer>
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={placeName}
            offerId={offerId}
            offerType={offerType}
            coordinates={offerCoordinates}
            variant="filled"
          />
        </AddToTripButtonContainer>
      )}
      {onSendMessage && cardType === 'hotel' && !isBooked && !unavailableReason && (
        <AddToTripButtonContainer>
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={placeName}
            offerId={offerId}
            offerType={offerType}
            hotelCity={offerCity}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
            checkIn={selectedDatesAndRoomsFilter?.checkIn}
            checkOut={selectedDatesAndRoomsFilter?.checkOut}
            minNights={getHotelOfferMinNights(offer)}
          />
        </AddToTripButtonContainer>
      )}
      {onSendMessage && cardType === 'cruise' && itineraryData && !isBooked && (
        <AddToTripButtonContainer>
          <ChatCruiseAddToTripAction
            offerId={offerId}
            cruise={cruiseOffer}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            isStreaming={isStreaming}
          />
        </AddToTripButtonContainer>
      )}
      <ErrorBoundary fallback={null}>
        <LoaderPlayStateContext.Provider value={pauseLoader ? 'paused' : 'running'}>
          <CarouselOfferCard ref={tileInViewRef} className={clsx(`size-${size}`, className)} data-testid="offer-card">
            {!!offer && <OfferListEventsProvider onListEvent={onListEvent}>
              <CarouselPricingSourceContext.Provider value={isOfferDetailSource}>
                <OfferCardMapper
                  offer={offer}
                  idx={index}
                  variant="card-default"
                  size={size}
                  selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
                  className={className}
                  PricingComponent={ChatHotelCarouselPrice}
                  BedbankPricingComponent={isOfferDetailSource && selectedDatesAndRoomsFilter?.flexibleNights ? FlexibleBedbankPricing : undefined}
                  CtaComponent={isNoDatePriceWithheld ? SelectDatesCta : undefined}
                />
              </CarouselPricingSourceContext.Provider>
            </OfferListEventsProvider>}
          </CarouselOfferCard>
        </LoaderPlayStateContext.Provider>
      </ErrorBoundary>
    </HotelCardWrapper>
  )
}

export default memo(ChatHotelCarouselCard)
